// Color design tokens.
//
// Each ramp is defined once, lightest (50/100) → darkest (900/950). Dark mode
// uses the ramps as-is; light mode inverts them so that e.g. `grey[100]` is
// always "the strongest foreground colour" and `grey[900]` "the faintest
// surface" regardless of mode. Components that call `tokens(mode)` directly
// rely on that convention, so keep it when adding ramps.

const ramps = {
  // Slate neutrals. 1000 is the extreme surface (page bg in dark, pure white in light).
  grey: {
    dark: {
      100: "#F1F5F9",
      200: "#CBD5E1",
      300: "#B6C2D2",
      400: "#8B98AB",
      500: "#5B6779",
      600: "#3A4658",
      700: "#263244",
      800: "#1A2332",
      900: "#111827",
      1000: "#0B1120",
    },
    light: {
      100: "#0F172A",
      200: "#475569",
      300: "#5B6B82",
      400: "#7C8BA1",
      500: "#94A3B8",
      600: "#CBD5E1",
      700: "#E2E8F0",
      800: "#EEF2F6",
      900: "#F4F6F9",
      1000: "#FFFFFF",
    },
  },
  // Orbit blue: a muted cobalt/navy rather than Tailwind's saturated blue.
  // 600 is the light-mode main (8.5:1 with white), 400 the dark-mode main.
  primary: {
    100: "#E4EBF5",
    200: "#C5D3E8",
    300: "#9AB2D6",
    400: "#6F90C2",
    500: "#3F68A6",
    600: "#1F4B8E",
    700: "#1A3F78",
    800: "#163362",
    900: "#12284D",
  },
  greenAccent: {
    100: "#DCFCE7",
    200: "#BBF7D0",
    300: "#86EFAC",
    400: "#4ADE80",
    500: "#22C55E",
    600: "#16A34A",
    700: "#15803D",
    800: "#166534",
    900: "#14532D",
  },
  redAccent: {
    100: "#FEE2E2",
    200: "#FECACA",
    300: "#FCA5A5",
    400: "#F87171",
    500: "#EF4444",
    600: "#DC2626",
    700: "#B91C1C",
    800: "#991B1B",
    900: "#7F1D1D",
  },
  // Sky — used for "info"
  blueAccent: {
    100: "#E0F2FE",
    200: "#BAE6FD",
    300: "#7DD3FC",
    400: "#38BDF8",
    500: "#0EA5E9",
    600: "#0284C7",
    700: "#0369A1",
    800: "#075985",
    900: "#0C4A6E",
  },
  pinkAccent: {
    100: "#FCE7F3",
    200: "#FBCFE8",
    300: "#F9A8D4",
    400: "#F472B6",
    500: "#EC4899",
    600: "#DB2777",
    700: "#BE185D",
    800: "#9D174D",
    900: "#831843",
  },
  // Amber — used for "warning"
  yellowAccent: {
    50: "#FFFBEB",
    100: "#FEF3C7",
    200: "#FDE68A",
    300: "#FCD34D",
    400: "#FBBF24",
    500: "#F59E0B",
    600: "#D97706",
    700: "#B45309",
    800: "#92400E",
    900: "#78350F",
    950: "#451A03",
  },
};

// Mirror a ramp around its midpoint: 100 ↔ 900, 200 ↔ 800, 50 ↔ 950, …
const invert = (ramp) => {
  const keys = Object.keys(ramp).map(Number).sort((a, b) => a - b);
  return Object.fromEntries(keys.map((k, i) => [k, ramp[keys[keys.length - 1 - i]]]));
};

const forMode = (mode, ramp) => (mode === "dark" ? ramp : invert(ramp));

export const tokens = (mode) => ({
  grey: ramps.grey[mode === "dark" ? "dark" : "light"],
  primary: forMode(mode, ramps.primary),
  greenAccent: forMode(mode, ramps.greenAccent),
  redAccent: forMode(mode, ramps.redAccent),
  blueAccent: forMode(mode, ramps.blueAccent),
  pinkAccent: forMode(mode, ramps.pinkAccent),
  yellowAccent: forMode(mode, ramps.yellowAccent),
});
